// Suppression des traceurs déjà déposés dans le navigateur du visiteur.
//
// Deux besoins distincts, d'où deux fonctions :
//
// - `clearLegacyAnalyticsCookies()` nettoie ce que Google Analytics a laissé
//   derrière lui. Retirer le script de `public/index.html` (#75) arrête la
//   collecte mais n'efface rien : `_ga` a une durée de vie de deux ans, un
//   visiteur de retour le porterait donc encore longtemps après le retrait.
// - `clearAnalyticsCookies()` nettoie la mesure d'audience en place dès que le
//   visiteur retire son consentement. La CNIL exige qu'un retrait soit aussi
//   effectif qu'un accord : la collecte doit cesser et ses traceurs disparaître
//   sans attendre un rechargement de page.

const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT'

const GOOGLE_ANALYTICS_PREFIXES = ['_ga', '_gid', '_gat']

// PostHog (#77) : cookie de session `ph_<clé>_posthog`, plus les clés
// `__ph_opt_in_out_*`. Les préfixes couvrent aussi ce que la bibliothèque
// range en `localStorage` ET en `sessionStorage` — l’identifiant de fenêtre
// (`ph_<clé>_window_id`) y vit, et il traverserait un retrait de consentement
// sans cette purge.
const POSTHOG_PREFIXES = ['ph_', '__ph_']

// Un cookie ne s'efface qu'en le réécrivant avec le couple (domaine, chemin)
// exact qui l'a posé : sinon le navigateur crée un second cookie homonyme, déjà
// expiré, et laisse l'original intact. gtag pose sur le domaine parent
// (`.azoya-spa.com`), d'autres traceurs sur l'hôte exact — on balaie donc tous
// les candidats, du plus précis au plus large, en s'arrêtant avant le TLD.
function candidateDomains() {
  const { hostname } = window.location
  const domains = [null, hostname, `.${hostname}`]
  const labels = hostname.split('.')
  for (let index = 1; index < labels.length - 1; index += 1) {
    domains.push(`.${labels.slice(index).join('.')}`)
  }
  return domains
}

function deleteCookie(name) {
  for (const domain of candidateDomains()) {
    const attributes = `expires=${EXPIRED}; path=/${domain ? `; domain=${domain}` : ''}`
    document.cookie = `${name}=; ${attributes}`
  }
}

function deleteCookiesWithPrefixes(prefixes) {
  const names = document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0].trim())
    .filter(Boolean)

  for (const name of names) {
    if (prefixes.some((prefix) => name.startsWith(prefix))) deleteCookie(name)
  }
}

// `getStorage` est un accesseur, pas une valeur déjà lue : `window.localStorage`
// et `window.sessionStorage` peuvent lever à la simple LECTURE de la
// propriété (navigation privée, stockage désactivé), pas seulement sur
// `getItem`/`setItem`. Passer la valeur toute faite ferait échouer cette
// lecture avant l’entrée dans le `try` ci-dessous, hors de portée du `catch`.
function purgeStorage(getStorage, label, prefixes) {
  let storage
  let keys
  try {
    storage = getStorage()
    keys = Object.keys(storage)
  } catch (error) {
    // Stockage indisponible : il n'a donc rien pu y être écrit non plus.
    return
  }

  for (const key of keys) {
    if (!prefixes.some((prefix) => key.startsWith(prefix))) continue
    try {
      storage.removeItem(key)
    } catch (error) {
      console.error(`Traceur « ${key} » non supprimé du stockage ${label}`, error)
    }
  }
}

// localStorage et sessionStorage sont balayés par deux appels séparés, chacun
// avec son propre `try` (voir purgeStorage) : un navigateur en navigation
// privée peut autoriser l’un et refuser l’autre, et celui qui reste
// accessible doit quand même être purgé.
function deleteStorageWithPrefixes(prefixes) {
  purgeStorage(() => window.localStorage, 'local', prefixes)
  purgeStorage(() => window.sessionStorage, 'de session', prefixes)
}

/** Efface les cookies laissés par Google Analytics, retiré du site. */
export function clearLegacyAnalyticsCookies() {
  deleteCookiesWithPrefixes(GOOGLE_ANALYTICS_PREFIXES)
}

/**
 * Efface les traceurs de mesure d'audience. Idempotent : appeler la fonction
 * alors que rien n'a été déposé ne fait rien, ce qui permet de l'appeler à
 * chaque refus sans avoir à comparer avec l'état précédent.
 */
export function clearAnalyticsCookies() {
  deleteCookiesWithPrefixes(POSTHOG_PREFIXES)
  deleteStorageWithPrefixes(POSTHOG_PREFIXES)
}
